import { _notNil } from '@/littledash.ts';
import { StudyApiId } from '@/model/Study.model.ts';
import { ObservationRecordBulkNormalV1, ObservationRecordBulkV1 } from '@/model/Observation.model.ts';
import { TeamGlossaryApiId } from '@/model/Glosary.model.ts';

export interface ObservationRowData {
  observationType: string;
  team_glossary_api_id: TeamGlossaryApiId;
  type: 'numeric' | 'severity' | 'none';
  score: number | ''; // For numeric type
  severity: 'Mild' | 'Moderate' | 'Severe' | '';
  comments: string;
  description?: string; // Description for tooltip
  options?: { min: string; max: string };
}

export const submitBulkObservations = async (
  studyApiId: StudyApiId,
  animalApiIds: Array<string>,
  observationsData: Array<ObservationRowData>,
  observed_at: string,
  invokeApi: (params: { body: ObservationRecordBulkV1; path: { studyApiId: StudyApiId } }) => Promise<unknown>
): Promise<void> => {
  const observations = observationsData.map((obs) => ({
    team_glossary_api_id: obs.team_glossary_api_id,
    observed_at,
    text: obs.comments || null,
    value: obs.type === 'numeric' ? String(obs.score) : obs.severity || null,
  }));

  const payload: ObservationRecordBulkV1 = {
    animal_api_ids: animalApiIds,
    observations,
  };

  await invokeApi({
    body: payload,
    path: { studyApiId },
  });
};

export const submitNormalObservations = async (
  animalApiIds: Array<string>,
  studyApiId: StudyApiId,
  observed_at: string,
  invokeNormalApi: (params: {
    body: ObservationRecordBulkNormalV1;
    path: { studyApiId: StudyApiId };
  }) => Promise<unknown>
): Promise<void> => {
  await invokeNormalApi({
    body: { animal_api_ids: animalApiIds, observed_at },
    path: { studyApiId },
  });
};

export const validateObservationData = (observationsData: Array<ObservationRowData>): boolean => {
  for (const obs of observationsData) {
    // For numeric observations, validate score is provided and within range
    if (obs.type === 'numeric') {
      if (obs.score === '') {
        return false;
      }

      if (_notNil(obs.options)) {
        const score = Number(obs.score);
        const min = Number(obs.options.min);
        const max = Number(obs.options.max);

        if (_notNil(min) && score < min) {
          return false;
        }
        if (_notNil(max) && score > max) {
          return false;
        }
      }
    }

    // For severity observations, validate severity is selected
    if (obs.type === 'severity') {
      if (obs.severity === '') {
        return false;
      }
    }
  }

  return true;
};
